import { useRef, useState, type FormEvent } from 'react';
import type { AuthError } from '@supabase/supabase-js';
import { ShieldAlert } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useSession } from '../lib/session';
import { t } from '../lib/sprache';
import { Logo } from '../ui/logo';
import { Turnstile, type TurnstileRef } from '../ui/turnstile';

/** Ergebnis einer fehlgeschlagenen Anmeldung: entweder die Sicherheitsprüfung (Cloudflare) oder das Konto */
type Anmeldefehler = { art: 'pruefung' | 'konto'; text: string };

/**
 * Übersetzt die Antwort von Supabase Auth in eine verständliche Meldung.
 * Supabase unterscheidet bewusst nicht zwischen unbekannter E-Mail und falschem Passwort
 * (Schutz gegen das Ausforschen von Konten), deshalb eine gemeinsame Meldung dafür.
 */
function anmeldefehler(error: AuthError): Anmeldefehler {
  const code = (error as AuthError & { code?: string }).code ?? '';
  const text = error.message ?? '';
  if (code === 'captcha_failed' || /captcha/i.test(text)) {
    // Abgelaufen oder doppelt verwendet: der Benutzer kann es selbst lösen
    if (/timeout-or-duplicate/i.test(text)) {
      return { art: 'pruefung', text: t('Die Sicherheitsprüfung von Cloudflare ist abgelaufen. Bitte erneut anmelden.') };
    }
    // Alles andere (z. B. invalid-input-secret) ist ein Einrichtungsfehler auf Serverseite
    return { art: 'pruefung', text: t('Die Sicherheitsprüfung von Cloudflare wurde vom Server abgelehnt. E-Mail und Passwort wurden nicht geprüft. Bitte parlinex informieren.') };
  }
  if (code === 'invalid_credentials' || /invalid login credentials/i.test(text)) {
    return { art: 'konto', text: t('E-Mail-Adresse oder Passwort ist falsch.') };
  }
  if (code === 'email_not_confirmed') return { art: 'konto', text: t('Die E-Mail-Adresse ist noch nicht bestätigt. Bitte den Link aus der Einladung öffnen.') };
  if (code === 'user_banned') return { art: 'konto', text: t('Dieses Konto ist gesperrt. Bitte wende dich an parlinex.') };
  if (code === 'over_request_rate_limit' || error.status === 429) return { art: 'konto', text: t('Zu viele Anmeldeversuche. Bitte einige Minuten warten.') };
  return { art: 'konto', text: t('Anmeldung fehlgeschlagen. Bitte E-Mail und Passwort prüfen.') };
}

export default function Anmelden() {
  const { hostMandant, hinweis, hinweisLoeschen } = useSession();
  const [email, setEmail] = useState('');
  const [passwort, setPasswort] = useState('');
  /** Meldung zum Konto (Zugangsdaten, Sperre, Zugang zum Mandanten) */
  const [fehler, setFehler] = useState<string | null>(null);
  /** Meldung der Sicherheitsprüfung (Cloudflare Turnstile), getrennt vom Konto */
  const [pruefung, setPruefung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [captcha, setCaptcha] = useState<string | null>(null);
  const widget = useRef<TurnstileRef>(null);

  async function absenden(e: FormEvent) {
    e.preventDefault();
    if (!captcha) return;
    setFehler(null);
    setPruefung(null);
    hinweisLoeschen();
    setLaeuft(true);
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password: passwort, options: { captchaToken: captcha } });
    setLaeuft(false);
    // Ein Token gilt nur einmal
    widget.current?.zuruecksetzen();
    if (!error) return;
    const f = anmeldefehler(error);
    if (f.art === 'pruefung') setPruefung(f.text);
    else setFehler(f.text);
  }

  const kontoMeldung = fehler ?? hinweis;

  return (
    <main className="anmelden">
      <form className="karte" onSubmit={absenden} data-tour="anmelden-formular">
        <h1 className="anmelden-logo"><Logo hoehe={40} /></h1>
        <p className="leise">
          {hostMandant ? t('Anmeldung für {mandant}', { mandant: hostMandant.name }) : t('Anmeldung für eingeladene Nutzer')}
        </p>
        <label>
          {t('E-Mail')}
          <input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label>
          {t('Passwort')}
          <input type="password" autoComplete="current-password" required value={passwort} onChange={(e) => setPasswort(e.target.value)} />
        </label>
        {kontoMeldung && <p className="fehler" role="alert">{kontoMeldung}</p>}
        <Turnstile ref={widget} aktion="anmeldung" onToken={setCaptcha} onFehler={(text) => setPruefung(t(text))} />
        {pruefung && (
          <div className="hinweis hinweis-warn" role="alert">
            <ShieldAlert aria-hidden="true" />
            <span>{pruefung}</span>
          </div>
        )}
        <button className="kn kn-primaer" type="submit" disabled={laeuft || !captcha}>
          {laeuft ? t('Anmelden …') : captcha ? t('Anmelden') : t('Sicherheitsprüfung …')}
        </button>
      </form>
    </main>
  );
}
